import { getActiveKindergartensForAdmin, getLocationsWIthGroups } from '../../api/kindergarten.api';
import { getKindergartenFeatures, KindergartenFeatureDto } from '../../api/feature.api';
import { KindergartenBasicInfoDto, LocationDto } from '../../common/interfaces/kindergarten.interface';

/**
 * The two lists every Finance screen navigates by: which kindergartens this admin may see,
 * and the locations and groups inside one of them.
 *
 * The page and its breadcrumb both need the same lists on the same render, so requests are
 * shared rather than fired twice. Entries expire after a short while instead of living for
 * the whole session: a location added in another tab should show up on the next screen,
 * not after a reload.
 */
const TTL_MS = 30_000;

interface Entry<T> {
  promise: Promise<T>;
  at: number;
}

const cache = new Map<string, Entry<unknown>>();

const cached = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const hit = cache.get(key) as Entry<T> | undefined;
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;

  const promise = load().catch(error => {
    // a failure must not be remembered, or one bad request blanks every screen for 30s
    cache.delete(key);
    throw error;
  });
  cache.set(key, { promise, at: Date.now() });
  return promise;
};

export const loadAdminKindergartens = (): Promise<KindergartenBasicInfoDto[]> =>
  cached('kindergartens', async () => (await getActiveKindergartensForAdmin()).results ?? []);

/** Every location, whatever its status - the Finance totals include archived ones too. */
export const loadKindergartenHierarchy = (kindergartenId: string): Promise<LocationDto[]> =>
  cached(`hierarchy:${kindergartenId}`, async () => (await getLocationsWIthGroups(kindergartenId)).results ?? []);

/** Which features one kindergarten has; the sidebar asks for every kindergarten at once. */
export const loadKindergartenFeatures = (kindergartenId: string): Promise<KindergartenFeatureDto[]> =>
  cached(`features:${kindergartenId}`, () => getKindergartenFeatures(kindergartenId));

/** For tests, which must not see each other's responses. */
export const clearFinanceDirectory = (): void => cache.clear();
